'use client'

import { useEffect } from 'react'

import { useRouter } from 'next/navigation'

import { useAuthToken } from '@core/hooks/useCookie'
import { useAPI } from '../hooks/useAPI'
import i18n, { defaultLang } from '@/core/i18n/setting'
import { useAuthStore } from './useAuthManager'

export interface User {
  id: string
  email: string
  firstName?: string
  lastName?: string
  phoneNumber?: string
  profilePhoto?: string
  [key: string]: any
}

export function useAuth() {
  const router = useRouter()
  const [token, setAuthToken, clearAuthToken] = useAuthToken()

  const { setUser, setToken, setLoading, user, isAuthenticated } = useAuthStore()

  const { data, isLoading } = useAPI(['auth', 'getProfile'], {
    enabled: isAuthenticated
  })

  const logout = () => {
    try {
      const lang = (typeof window !== 'undefined' && i18n?.language) || defaultLang
      const maxAge = 60 * 60 * 24 * 7 // 1 week

      // Set both cookies that middleware checks: NEXT_LOCALE and i18next
      document.cookie = `NEXT_LOCALE=${lang};path=/;max-age=${maxAge}`
      document.cookie = `i18next=${lang};path=/;max-age=${maxAge}`
    } catch (err) {
      // ignore cookie set errors in non-browser environments
    }

    // Clear auth cookie/token
    clearAuthToken()

    try {
      setUser(null)
      setToken(null)

      localStorage.removeItem('auth-storage')
      localStorage.removeItem('user')
    } catch (e) {
      // ignore errors in non-browser environments
    }

    router.push('/login')
  }

  const login = (authToken: string, userData?: User) => {
    setAuthToken(authToken)
    setToken(authToken)

    if (userData) {
      setUser(userData)
      localStorage.setItem('user', JSON.stringify(userData))
    }
  }

  useEffect(() => {
    const initialize = async () => {
      if (!token) {
        setUser(null)
        setToken(null)
        setLoading(false)

        return
      }

      try {
        const local = localStorage.getItem('user')
        const storedUser = local ? JSON.parse(local) : null

        if (data?.data) {
          const mergedUser = { ...storedUser, ...data.data }

          setUser(mergedUser)
          localStorage.setItem('user', JSON.stringify(mergedUser))
        } else if (storedUser) {
          setUser(storedUser)
        }
      } catch (err) {
        console.error('Auth init error:', err)
        clearAuthToken()
        setUser(null)
        setToken(null)
        localStorage.removeItem('user')
      } finally {
        setLoading(false)
      }
    }

    initialize()
  }, [token, data, clearAuthToken, setUser, setToken, setLoading])

  return { token, setAuthToken, logout, user, isLoading, login, isAuthenticated }
}
